<template>
  <div
    v-if="campaigns.length"
    class="w-full bg-black text-white text-xs py-2 sm:py-3 px-3 sm:px-4 flex justify-center items-center gap-3 sm:gap-6 overflow-x-auto text-center border-b border-black"
    style="min-height: 44px;"
  >
    <span class="font-semibold whitespace-nowrap">PREORDER</span>
    <template v-for="campaign in campaigns" :key="campaign.id">
      <div class="flex items-center gap-1 sm:gap-0">
        <NuxtLink :to="getProductLink(campaign)" class="underline hover:text-gray-300 transition whitespace-nowrap">
          {{ campaign.title }}
        </NuxtLink>
        <span v-if="timeLeft[campaign.id]" class="text-gray-300 text-xs sm:text-xs">
          <span class="hidden sm:inline">·</span>
          <span class="sm:inline">{{ timeLeft[campaign.id] }}</span>
        </span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

const campaigns = ref<any[]>([])
const timeLeft = ref<Record<string, string>>({})
let timer: any = null
let idleHandle: number | null = null
let fallbackTimer: ReturnType<typeof setTimeout> | null = null

async function fetchCampaigns() {
  const { useSupabaseClient } = await import('~/composables/useSupabaseClient')
  const supabase = useSupabaseClient()
  const { data, error } = await supabase
    .from('preorder_campaigns')
    .select('*')
    .eq('status', 'active')
    .order('end_date', { ascending: true })
  if (!error && data) {
    const now = new Date()
    campaigns.value = data.filter((c) => {
      if (!c.end_date) return false
      return now < new Date(c.end_date)
    })
  }
}

function updateTimeLeft() {
  const now = new Date()
  const result: Record<string, string> = {}
  for (const campaign of campaigns.value) {
    if (campaign.end_date) {
      const end = new Date(campaign.end_date)
      let diff = end.getTime() - now.getTime()
      if (diff <= 0) {
        result[campaign.id] = 'ended'
        continue
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      diff -= days * 1000 * 60 * 60 * 24
      const hours = Math.floor(diff / (1000 * 60 * 60))
      diff -= hours * 1000 * 60 * 60
      const minutes = Math.floor(diff / (1000 * 60))
      diff -= minutes * 1000 * 60
      const seconds = Math.floor(diff / 1000)
      let label = ''
      if (days > 0) {
        label += `${days}d `
      }
      if (hours > 0) {
        label += `${hours}h `
      }
      if (minutes > 0) {
        label += `${minutes}m `
      }
      if (days === 0 && hours === 0 && minutes === 0 && seconds > 0) {
        label += `${seconds}s `
      }
      if (label.trim() === '') {
        label = 'ending soon'
      } else if (label.trim().endsWith('s')) {
        label += 'left'
      }
      result[campaign.id] = label.trim()
    }
  }
  timeLeft.value = result
}

onMounted(() => {
  const startCampaignBootstrap = () => {
    fetchCampaigns().then(() => {
      updateTimeLeft()
      timer = setInterval(updateTimeLeft, 1000)
    })
  }

  if ('requestIdleCallback' in window) {
    idleHandle = window.requestIdleCallback(() => {
      startCampaignBootstrap()
    }, { timeout: 2000 })
    return
  }

  fallbackTimer = setTimeout(() => {
    startCampaignBootstrap()
  }, 1200)
})

onUnmounted(() => {
  if (idleHandle && 'cancelIdleCallback' in window) {
    window.cancelIdleCallback(idleHandle)
    idleHandle = null
  }
  if (fallbackTimer) {
    clearTimeout(fallbackTimer)
    fallbackTimer = null
  }
  if (timer) {
    clearInterval(timer)
    timer = null
  }
})

function getProductLink(campaign: any) {
  // Generate product link based on campaign data
  if (campaign.product_type && campaign.color) {
    return `/${campaign.product_type}/${campaign.color}`
  }
  return `/${campaign.product_type}`
}
</script>

<style scoped>
.fixed {
  left: 0;
  right: 0;
}
</style> 
